// Advanced student-list filter drawer (admin).
//
// Lives behind a single "Filters" button in the admin Students tab. Clicking
// opens a right-side sheet with every filter grouped by section (Who · Where
// · When). Each option shows a live count — e.g. "Jul 2026 · 41" — so admins
// know how many students they're about to pull BEFORE applying.
//
// The parent owns the filter *state* (so the Dashboard can keep reusing
// existing search/export logic). We just render inputs that call the parent's
// setters.
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../ui/sheet";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { API } from "@/config/api";
import { FunnelSimple, X, CircleNotch } from "@phosphor-icons/react";

/** Appearance-only helper to render a scrollable list of options with counts. */
function CountedOptionList({ options, value, onPick, loading, allLabel = "Any" }) {
  return (
    <div className="rounded-md border border-gray-200 overflow-hidden">
      <button type="button" onClick={() => onPick("all")}
        className={`w-full flex items-center justify-between px-3 py-2 text-xs border-b border-gray-100 hover:bg-gray-50 ${value === "all" || !value ? "bg-indigo-50 text-indigo-800 font-medium" : "text-gray-700"}`}>
        <span>{allLabel}</span>
        <span className="text-gray-400">{loading ? "…" : options.reduce((a, b) => a + b.count, 0)}</span>
      </button>
      <div className="max-h-56 overflow-y-auto divide-y divide-gray-100">
        {loading && <div className="p-3 text-[11px] text-gray-500">Loading counts…</div>}
        {!loading && options.length === 0 && (
          <div className="p-3 text-[11px] text-gray-500">No matches with current filters.</div>
        )}
        {options.map((opt) => (
          <button key={opt.key || opt.value || "_blank"} type="button"
            onClick={() => onPick(opt.key || opt.value)}
            className={`w-full flex items-center justify-between px-3 py-2 text-xs hover:bg-gray-50 ${value === (opt.key || opt.value) ? "bg-indigo-50 text-indigo-800 font-medium" : "text-gray-700"}`}>
            <span className="truncate text-left pr-2">{opt.label || opt.value || "(unassigned)"}</span>
            <span className="text-gray-400 tabular-nums">{opt.count}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function StudentsFilterDrawer({
  open, onOpenChange,
  filters, setFilters,
  recruiters = [], colleges = [],
}) {
  const [counts, setCounts] = useState({ intake: [], recruiter: [], college: [], status: [], application_stage: [] });
  const [loading, setLoading] = useState(false);

  const otherParams = () => {
    const p = new URLSearchParams();
    if (filters.recruiter_id !== "all") p.set("recruiter_id", filters.recruiter_id);
    if ((filters.status || []).length) p.set("status", filters.status.join(","));
    if (filters.college_id !== "all") p.set("college_id", filters.college_id);
    if (filters.intake && filters.intake !== "all") p.set("intake", filters.intake);
    if (filters.search) p.set("search", filters.search);
    if (filters.start_date) p.set("start_date", filters.start_date);
    if (filters.end_date) p.set("end_date", filters.end_date);
    return p;
  };

  // Fetch live per-value counts for each filter whenever the drawer opens or
  // another filter changes. We hit the backend facet endpoint — it excludes
  // the field being faceted on from its own count.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    const fields = ["intake", "recruiter", "college", "status"];
    (async () => {
      try {
        const next = { ...counts };
        await Promise.all(fields.map(async (field) => {
          const p = otherParams();
          p.set("field", field);
          try {
            const { data } = await axios.get(`${API}/admin/students/filter-counts?${p}`);
            next[field] = data.counts || [];
          } catch { next[field] = []; }
        }));
        if (!cancelled) setCounts(next);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, filters.recruiter_id, filters.status, filters.college_id, filters.intake, filters.search, filters.start_date, filters.end_date]);

  // Enrich counts with human labels.
  const recOptions = counts.recruiter.map((r) => {
    const match = recruiters.find((x) => x.id === r.value);
    return { key: r.value, value: r.value, label: match?.full_name || r.value || "(unassigned)", count: r.count };
  }).filter((o) => o.value);
  const collOptions = counts.college.map((c) => {
    const match = colleges.find((x) => x.id === c.value);
    return { key: c.value || "unassigned", value: c.value, label: match?.name || c.value || "(unassigned)", count: c.count };
  });
  const intakeOptions = counts.intake.map((i) => ({
    key: i.value || "unassigned", value: i.value, label: i.value || "(unassigned)", count: i.count,
  }));
  const statusOptions = counts.status.map((s) => ({
    key: s.value || "unassigned", value: s.value, label: s.value || "(unassigned)", count: s.count,
  }));

  const toggleStatus = (val) => {
    const cur = new Set(filters.status || []);
    if (val === "all") { setFilters({ ...filters, status: [] }); return; }
    if (cur.has(val)) cur.delete(val); else cur.add(val);
    setFilters({ ...filters, status: [...cur] });
  };

  const resetAll = () => setFilters({
    search: "", recruiter_id: "all", status: [], college_id: "all",
    intake: "all", start_date: "", end_date: "",
  });

  const activeCount = (
    (filters.search ? 1 : 0) +
    (filters.recruiter_id !== "all" ? 1 : 0) +
    ((filters.status || []).length > 0 ? 1 : 0) +
    (filters.college_id !== "all" ? 1 : 0) +
    (filters.intake && filters.intake !== "all" ? 1 : 0) +
    (filters.start_date || filters.end_date ? 1 : 0)
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="students-filter-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <FunnelSimple size={18} /> Filters
            {activeCount > 0 && <Badge className="bg-indigo-600 text-white">{activeCount}</Badge>}
            {loading && <CircleNotch size={13} className="animate-spin text-gray-400" />}
          </SheetTitle>
          <SheetDescription className="text-xs">
            Narrow the student list. Live counts update as you set filters.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-5 space-y-6">
          <section className="space-y-2">
            <Label className="text-[11px] uppercase tracking-wider text-gray-500">Search</Label>
            <Input placeholder="Name, email, program…" value={filters.search || ""}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              data-testid="drawer-search" />
          </section>

          <section className="space-y-2">
            <Label className="text-[11px] uppercase tracking-wider text-gray-500">Intake</Label>
            <CountedOptionList
              options={intakeOptions}
              value={filters.intake || "all"}
              onPick={(v) => setFilters({ ...filters, intake: v === "all" ? "all" : v })}
              loading={loading}
              allLabel="Any intake"
            />
          </section>

          <section className="space-y-2">
            <Label className="text-[11px] uppercase tracking-wider text-gray-500">Recruiter</Label>
            <CountedOptionList
              options={recOptions}
              value={filters.recruiter_id}
              onPick={(v) => setFilters({ ...filters, recruiter_id: v })}
              loading={loading}
              allLabel="Any recruiter"
            />
          </section>

          <section className="space-y-2">
            <Label className="text-[11px] uppercase tracking-wider text-gray-500">College</Label>
            <CountedOptionList
              options={collOptions}
              value={filters.college_id}
              onPick={(v) => setFilters({ ...filters, college_id: v })}
              loading={loading}
              allLabel="Any college"
            />
          </section>

          <section className="space-y-2">
            <Label className="text-[11px] uppercase tracking-wider text-gray-500">Status</Label>
            <div className="flex flex-wrap gap-1.5">
              <button type="button"
                onClick={() => toggleStatus("all")}
                className={`px-2.5 py-1 rounded-full text-[11px] border ${(filters.status || []).length === 0 ? "bg-indigo-600 border-indigo-600 text-white" : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"}`}>
                Any
              </button>
              {statusOptions.map((s) => {
                const on = (filters.status || []).includes(s.value);
                return (
                  <button key={s.key} type="button"
                    onClick={() => toggleStatus(s.value)}
                    className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${on ? "bg-indigo-600 border-indigo-600 text-white" : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"}`}>
                    {s.label} <span className="opacity-70 ml-1">· {s.count}</span>
                  </button>
                );
              })}
            </div>
          </section>

          <section className="space-y-2">
            <Label className="text-[11px] uppercase tracking-wider text-gray-500">Created date</Label>
            <div className="grid grid-cols-2 gap-2">
              <Input type="date" value={filters.start_date || ""}
                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} data-testid="drawer-start-date" />
              <Input type="date" value={filters.end_date || ""}
                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} data-testid="drawer-end-date" />
            </div>
          </section>

          <div className="flex items-center justify-between pt-2 border-t border-gray-100">
            <Button variant="ghost" onClick={resetAll} className="text-xs" data-testid="drawer-reset">
              <X size={13} className="mr-1" /> Reset
            </Button>
            <Button onClick={() => onOpenChange(false)} data-testid="drawer-apply">Done</Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
